import { isPlatformBrowser } from "@angular/common";
import {
    Directive,
    ElementRef,
    inject,
    OnDestroy,
    PLATFORM_ID,
} from "@angular/core";

/**
 * NgOptimizedImage removes the `loading` attribute when an <img> is destroyed
 * (angular/angular#67055). For an image that is `loading="lazy"` and not yet
 * loaded, this turns it into an eager one: the browser fetches it right away
 * even though the element is being removed.
 *
 * `ngOnDestroy` hooks run before `DestroyRef.onDestroy` callbacks
 * (executeOnDestroys then processCleanups in @angular/core's cleanUpView), so
 * this runs before NgOptimizedImage's removal and leaves it nothing to fetch.
 */
@Directive({ selector: "img[hImageDestroyGuard]" })
export class ImageDestroyGuardDirective implements OnDestroy {
    private readonly img =
        inject<ElementRef<HTMLImageElement>>(ElementRef).nativeElement;
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    public ngOnDestroy(): void {
        if (!this.isBrowser || this.img.complete) {
            return;
        }
        // `src` first: the selected `srcset` candidate stays in use (no new request),
        // then `srcset`: the image has no source left, so nothing can be fetched.
        this.img.removeAttribute("src");
        this.img.removeAttribute("srcset");
    }
}
